import React, { createContext, useContext, useState, useEffect } from 'react';
import { userAPI, adminAPI } from '../services/api';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load user from localStorage on initialization (if exists)
  useEffect(() => {
    if (isInitialized) return;
    
    // Check for admin session first
    const savedAdmin = localStorage.getItem('stakemind_admin');
    if (savedAdmin) {
      try {
        const parsedAdmin = JSON.parse(savedAdmin);
        parsedAdmin.createdAt = new Date(parsedAdmin.createdAt);
        setAdminUser(parsedAdmin);
        console.log('👨‍💼 Admin carregado da sessão');
        setIsInitialized(true);
        return;
      } catch (error) {
        console.error('Erro ao carregar admin salvo:', error);
        localStorage.removeItem('stakemind_admin');
      }
    }
    
    const savedUser = localStorage.getItem('stakemind_user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        parsedUser.key_expires_at = new Date(parsedUser.key_expires_at);
        parsedUser.created_at = new Date(parsedUser.created_at);
        
        // Verificar se a chave já está expirada ao carregar da sessão
        const isExpired = new Date() > parsedUser.key_expires_at;
        if (isExpired) {
          console.log('🚫 Chave expirada detectada ao carregar sessão - removendo usuário');
          localStorage.removeItem('stakemind_user');
          setUser(null);
        } else {
          setUser(parsedUser);
          console.log('👤 Usuário carregado da sessão:', parsedUser.email);
        }
      } catch (error) {
        console.error('Erro ao carregar usuário salvo:', error);
        localStorage.removeItem('stakemind_user');
      }
    }
    
    setIsInitialized(true);
  }, [isInitialized]);

  // Sistema de monitoramento contínuo de expiração
  useEffect(() => {
    if (!user) return;

    const checkExpiration = () => {
      const isExpired = new Date() > user.key_expires_at;
      if (isExpired) {
        console.log('🚫 CHAVE EXPIRADA DETECTADA - Forçando logout automático');
        console.log('👤 Usuário:', user.name);
        console.log('📅 Expirou em:', user.key_expires_at);
        
        // Força logout imediato
        setUser(null);
        localStorage.removeItem('stakemind_user');
        
        // Mostrar mensagem para o usuário
        alert('⚠️ Sua licença expirou.\n\nPara continuar usando o sistema, entre em contato para renovação.');
        window.location.reload();
      }
    };

    // Verificar imediatamente
    checkExpiration();

    // Verificar a cada 30 segundos se a chave expirou
    const intervalId = setInterval(checkExpiration, 30000);

    // Limpar interval quando o usuário mudar ou componente desmontar
    return () => clearInterval(intervalId);
  }, [user]);

  const loginWithKey = async (email, accessKey) => {
    console.log('🔐 ===== TENTATIVA DE LOGIN =====');
    console.log('📧 E-mail fornecido:', email);
    console.log('🔑 Chave fornecida:', accessKey);
    
    setLoading(true);
    try {
      const response = await userAPI.authenticateUser(email, accessKey);
      
      // Se a API retornou que está expirado, retornar com as informações de expiração
      if (response.expired) {
        console.log('❌ ASSINATURA EXPIRADA - API retornou expired:', response.expired_at);
        return { 
          success: false, 
          expired: true, 
          expired_at: response.expired_at,
          message: response.message || 'Sua assinatura expirou. Renove para continuar acessando.'
        };
      }
      
      if (response.success && response.user) {
        const userToLogin = {
          ...response.user,
          key_expires_at: new Date(response.user.key_expires_at),
          created_at: new Date(response.user.created_at)
        };
        
        // Verificar se a chave está expirada ANTES de fazer login (verificação adicional)
        const isExpired = new Date() > userToLogin.key_expires_at;
        if (isExpired) {
          console.log('❌ CHAVE EXPIRADA - Login bloqueado');
          return { 
            success: false, 
            expired: true, 
            expired_at: userToLogin.key_expires_at.toISOString(),
            message: 'Sua assinatura expirou. Renove para continuar acessando.'
          };
        }
        
        setUser(userToLogin);
        localStorage.setItem('stakemind_user', JSON.stringify(userToLogin));
        
        console.log('🎉 ===== LOGIN REALIZADO COM SUCESSO! =====');
        console.log('👤 Usuário logado:', userToLogin.name);
        console.log('📧 E-mail:', userToLogin.email);
        console.log('📅 Expira em:', userToLogin.key_expires_at);
        
        return { success: true };
      } else {
        console.log('❌ Login falhou:', response.message);
        return { success: false, expired: false, message: response.message || 'Credenciais inválidas' };
      }
    } catch (error) {
      console.error('💥 ERRO DURANTE O LOGIN:', error);
      return { success: false, expired: false, message: 'Erro de conexão. Tente novamente.' };
    } finally {
      setLoading(false);
    }
  };

  const adminLogin = async (email, password) => {
    console.log('👨‍💼 TENTATIVA DE LOGIN ADMIN:', email);
    
    setLoading(true);
    try {
      const response = await adminAPI.login(email, password);
      
      if (response.success && response.admin) {
        const admin = {
          ...response.admin,
          created_at: new Date(),
          role: 'admin'
        };
        
        setAdminUser(admin);
        localStorage.setItem('stakemind_admin', JSON.stringify(admin));
        console.log('🎉 LOGIN ADMIN REALIZADO COM SUCESSO');
        return true;
      } else {
        console.log('❌ CREDENCIAIS DE ADMIN INVÁLIDAS');
        return false;
      }
    } catch (error) {
      console.error('💥 ERRO DURANTE LOGIN ADMIN:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  const logout = (clearRememberMe = false) => {
    setUser(null);
    setAdminUser(null);
    localStorage.removeItem('stakemind_user');
    localStorage.removeItem('stakemind_admin');
    
    // Só limpar credenciais salvas se explicitamente solicitado
    if (clearRememberMe) {
      localStorage.removeItem('stakemind_remember_credentials');
      console.log('🚫 Credenciais salvas removidas do localStorage');
    }
  };

  // Função para atualizar dados do usuário (ex: após renovação)
  const refreshUserData = async () => {
    if (!user) return;
    
    try {
      // Re-autenticar para pegar dados atualizados
      const response = await userAPI.authenticateUser(user.email, user.access_key);
      
      if (response.success && response.user) {
        const updatedUser = {
          ...response.user,
          key_expires_at: new Date(response.user.key_expires_at),
          created_at: new Date(response.user.created_at)
        };
        
        setUser(updatedUser);
        localStorage.setItem('stakemind_user', JSON.stringify(updatedUser));
        
        console.log('🔄 Dados do usuário atualizados:', updatedUser.name);
        console.log('📅 Nova data de expiração:', updatedUser.key_expires_at);
        return true;
      }
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
    }
    return false;
  };

  const isAuthenticated = !!user || !!adminUser;
  const isAdmin = !!adminUser;
  const isKeyExpired = user ? new Date() > user.key_expires_at : false;
  
  // Função para calcular dias restantes
  const getDaysUntilExpiry = () => {
    if (!user || !user.key_expires_at) return 0;
    
    try {
      const now = new Date();
      const expiryDate = typeof user.key_expires_at === 'string' 
        ? new Date(user.key_expires_at) 
        : user.key_expires_at;
      
      const diffTime = expiryDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      console.log('📅 Cálculo de expiração:', { 
        now: now.toISOString(), 
        expires: expiryDate.toISOString(), 
        diffDays 
      });
      
      return Math.max(0, diffDays);
    } catch (error) {
      console.error('❌ Erro ao calcular dias até expiração:', error);
      return 0;
    }
  };
  
  const daysUntilExpiry = getDaysUntilExpiry();
  const isExpiringSoon = daysUntilExpiry <= 3 && daysUntilExpiry > 0;

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      adminUser,
      loginWithKey,
      adminLogin,
      logout,
      refreshUserData,
      isAuthenticated,
      isAdmin,
      isKeyExpired,
      daysUntilExpiry,
      isExpiringSoon,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};